import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Slider, Modal, ActionIcon, Text, Group, Stack, NumberInput, Switch, Checkbox } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getApiUrl } from '../../config/api';
import { 
  faPlay, 
  faPause, 
  faUpload, 
  faTrash, 
  faCut, 
  faCopy, 
  faVolumeUp, 
  faVolumeMute,
  faSearchPlus,
  faSearchMinus,
  faDownload,
  faStop,
  faMagic,
  faSave,
  faSliders,
  faArrowLeft,
  faPencilAlt,
  faMagicWandSparkles,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/Auth/AuthContext';
import Navigation from '../Home/Navigation';
import VerticalNav from '../Home/VerticalNav';
import AudioMixer from './AudioMixer';
import TabContainer from './components/TabContainer';
import SoundEffectsModal from './SoundEffectsModal';
import { SoundtrackManager } from './components/SoundtrackManager';
import AudioModal from './AudioModal';
import AudioSelectionModal from './AudioSelectionModal';
import { useTitleGeneration } from '../../context/TitleGenerationContext';
import { TitleGenerationModal } from '../Modal';
import styles from './SoundEditor.module.css';
import { useNotification } from '../../context/NotificationContext';
import { StepIndicatorNames } from '../../types';
import { loadAuthenticatedAudioBuffer } from '../../utils/audioProxyUtil';

import { AudioTrack } from './types';
import { downloadAudio } from '../../services/autoDownloadService';
import axios from 'axios';

// Helper functions for audio fade calculations
const calculateVolumeWithFades = (
  currentTime: number, 
  duration: number, 
  baseVolume: number, 
  fadeInDuration: number, 
  fadeOutDuration: number
): number => {
  let volume = baseVolume;
  
  // Apply fade in
  if (fadeInDuration > 0 && currentTime < fadeInDuration) {
    const fadeInProgress = currentTime / fadeInDuration;
    volume *= fadeInProgress;
  }
  
  // Apply fade out
  if (fadeOutDuration > 0 && currentTime > (duration - fadeOutDuration)) {
    const fadeOutStart = duration - fadeOutDuration;
    const fadeOutProgress = (currentTime - fadeOutStart) / fadeOutDuration;
    volume *= (1 - fadeOutProgress);
  }
  
  return Math.max(0, Math.min(volume, baseVolume));
};

const scheduleFadeAutomation = (
  gainNode: GainNode, 
  startTime: number, 
  track: AudioTrack, 
  when: number
) => {
  const { duration, volume: baseVolume, fadeInDuration, fadeOutDuration } = track;
  
  // Clear any existing automation
  gainNode.gain.cancelScheduledValues(when);
  
  // Set current volume
  const currentVolume = calculateVolumeWithFades(startTime, duration, baseVolume, fadeInDuration, fadeOutDuration);
  gainNode.gain.setValueAtTime(currentVolume, when);
  
  // Schedule fade in if we're still in fade in period
  if (fadeInDuration > 0 && startTime < fadeInDuration) {
    const fadeInEnd = when + (fadeInDuration - startTime);
    gainNode.gain.linearRampToValueAtTime(baseVolume, fadeInEnd);
  }
  
  // Schedule fade out
  if (fadeOutDuration > 0) {
    const fadeOutStart = duration - fadeOutDuration;
    if (startTime < fadeOutStart) {
      // We haven't reached fade out yet
      const fadeOutStartTime = when + (fadeOutStart - startTime);
      const fadeOutEndTime = fadeOutStartTime + fadeOutDuration;
      gainNode.gain.setValueAtTime(baseVolume, fadeOutStartTime);
      gainNode.gain.linearRampToValueAtTime(0, fadeOutEndTime);
    }
  }
};

interface AudioEditorProps {
  // No props needed - we'll get projectId from URL params
}

const AudioEditor: React.FC<AudioEditorProps> = () => {
  const { t } = useTranslation('soundEditor');
  
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate(); // Add navigate hook
  const audioUrl = searchParams.get('audioUrl');
  const { user, token } = useAuth(); // Add auth hook to get user ID and token
  const { openTitleModal } = useTitleGeneration(); // Add title generation hook
  
  // Utility functions - Define early to avoid hoisting issues
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${mins}:${secs.padStart(4, '0')}`;
  };

  const snapToGridValue = (value: number, gridSizeParam: number, snapEnabled: boolean): number => {
    if (!snapEnabled) return value;
    return Math.round(value / gridSizeParam) * gridSizeParam;
  };
  
  // Audio context and state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(60);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(50); // Fixed pixels per second for proper zoom
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [draggedTrack, setDraggedTrack] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);
  const [resizeMode, setResizeMode] = useState<'left' | 'right' | null>(null);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(1); // seconds
  const [masterVolume, setMasterVolume] = useState(1);
  const [isMasterMuted, setIsMasterMuted] = useState(false);
  const [showAudioMixer, setShowAudioMixer] = useState(false);
  const [showSoundEffectsModal, setShowSoundEffectsModal] = useState(false);
  const [soundEffectsRefreshTrigger, setSoundEffectsRefreshTrigger] = useState(0);
  const [shouldNavigateToContentCreation, setShouldNavigateToContentCreation] = useState(false);
  
  // AI Audio Modal states
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [audioModalType, setAudioModalType] = useState<'upload' | 'melody' | 'prompts'>('melody');
  
  // Audio Selection Modal states
  const [isAudioSelectionModalOpen, setIsAudioSelectionModalOpen] = useState(false);
  
  // Soundtrack status state
  const [soundtrackVideoUrl, setSoundtrackVideoUrl] = useState<string | null>(null);

  // Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const playingSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const masterGainRef = useRef<GainNode | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const timeRulerRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const animationFrameRef = useRef<number>();
  const playStartTimeRef = useRef<number>(0);
  const playStartOffsetRef = useRef<number>(0);
  const hasInitializedRef = useRef<boolean>(false);
  const hasShownProjectLoadedRef = useRef<boolean>(false); // Track if we've already initialized

  const { showNotification } = useNotification();

  // Project data state - consolidated into single object
  const [projectData, setProjectData] = useState<any>({
    title: "Untitled Project",
    finalAudio: null,
    originalAudioUrl: null,
    originalVideoUrl: null,
    generatedVideoUrl: null,
    captionedVideoUrl: null,
    thumbnail: null,
    hasCaptions: false,
    titleData: null,
    finalSoundAudio: null
  });
  const [isEditingProjectTitle, setIsEditingProjectTitle] = useState(false);

  // Debug finalAudio state
  useEffect(() => {
    console.log('🎯 finalAudio state changed:', projectData.finalAudio);
  }, [projectData.finalAudio]);

  // Handle updating the project title
  const handleUpdateProjectTitle = async (newTitle: string) => {
    if (!projectId || !newTitle.trim()) {
      setIsEditingProjectTitle(false);
      return;
    }
    
    try {
      const response = await fetch(getApiUrl(`/projects/${projectId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project title');
      }
      
      setProjectData(prev => ({ ...prev, title: newTitle.trim() }));
      setIsEditingProjectTitle(false);
      
      // showNotification({
      //   title: 'Success',
      //   message: 'Project title updated successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Error updating project title:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to update project title',
        type: 'error'
      });
      setIsEditingProjectTitle(false);
    }
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    navigate('/sound-generation');
  };

  // Load project tracks from API
  const loadProjectTracks = async (): Promise<boolean> => {
    if (!projectId) return false;
    
    setIsLoading(true);
    
    try {
      // Determine track type based on current URL/path
      // audio-editor should load 'audio' tracks, sound-editor should load 'sound' tracks
      const currentPath = window.location.pathname;
      const trackType = currentPath.includes('audio-editor') ? 'audio' : 'sound';
      
      console.log(`🎵 Loading ${trackType} tracks for ${currentPath}`);
      
      const response = await fetch(getApiUrl(`/music-generation/projects/${projectId}/tracks?type=${trackType}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No tracks found, this is fine for new projects
          return false;
        }
        throw new Error('Failed to load project tracks');
      }

      const result = await response.json();
      
      if (result.success && result.data.tracks && result.data.tracks.length > 0) {
        const loadedTracks = result.data.tracks.map((track: any) => {
          // Detect if this is a video track based on track name or metadata
          const isVideo = track.name.toLowerCase().includes('video') || 
                          track.name.toLowerCase().includes('frame') ||
                          track.trackType === 'video' ||
                          track.sourceType === 'video';
          
          
          return {
            id: track.id,
            name: track.name,
            url: track.s3Url, // Backend uses 's3Url', frontend uses 'url'
            audioBuffer: null, // Will be loaded when needed
            startTime: track.startTime,
            duration: track.duration,
            originalDuration: track.originalDuration,
            volume: track.volume,
            isMuted: track.isMuted,
            color: track.color,
            laneIndex: track.laneIndex,
            fadeInDuration: track.fadeInDuration,
            fadeOutDuration: track.fadeOutDuration,
            trimStart: track.trimStart,
            trimEnd: track.trimEnd,
            isSelected: track.isSelected,
            isVideo: isVideo, // Mark video tracks
            isLocked: isVideo  // Lock video tracks from editing
          };
        });

        // Check if we have any tracks at all (including invalid ones)
        const hasAnyTracks = loadedTracks.length > 0;
        
        // Filter out tracks with blob URLs and warn the user
        const validTracks = loadedTracks.filter((track: AudioTrack) => {
          if (track.url.startsWith('blob:')) {
            console.warn(`Filtering out track "${track.name}" with invalid blob URL`);
            return false;
          }
          return true;
        });

        const invalidTrackCount = loadedTracks.length - validTracks.length;
        
        setTracks(validTracks);
        
        // Load editor settings from metadata
        const metadata = result.data.metadata || {};
        if (metadata.masterVolume !== undefined) setMasterVolume(metadata.masterVolume);
        if (metadata.isMasterMuted !== undefined) setIsMasterMuted(metadata.isMasterMuted);
        if (metadata.totalDuration !== undefined) setTotalDuration(metadata.totalDuration);
        if (metadata.zoomLevel !== undefined) setZoomLevel(metadata.zoomLevel);
        if (metadata.snapToGrid !== undefined) setSnapToGrid(metadata.snapToGrid);
        if (metadata.gridSize !== undefined) setGridSize(metadata.gridSize);

        if (invalidTrackCount > 0) {
          showNotification({
            title: 'Some Tracks Unavailable',
            message: `${invalidTrackCount} track(s) could not be loaded due to corrupted references. ${validTracks.length > 0 ? `Loaded ${validTracks.length} valid tracks.` : 'Loading original audio as fallback.'}`,
            type: 'warning'
          });
        } else if (validTracks.length > 0 && !hasShownProjectLoadedRef.current) {
          hasShownProjectLoadedRef.current = true;
          // showNotification({
          //   title: 'Project Loaded',
          //   message: `Loaded ${validTracks.length} audio tracks`,
          //   type: 'success'
          // });
        }
        
        // Return true if we have valid tracks, false if we need to fallback to URL loading
        return validTracks.length > 0;
      }
      
      return false; // No tracks found
    } catch (error) {
      console.error('Load error:', error);
      showNotification({
        title: 'Load Failed',
        message: 'Failed to load project tracks. Starting with empty project.',
        type: 'warning'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  // Combined loading effect - prioritize saved tracks over URL audio
  useEffect(() => {
    const initializeProject = async () => {
      if (!projectId) return;
      
      // Check if we've already loaded audio for this project
      // Only prevent re-initialization if we have existing tracks AND no video URLs have changed
      const videoDataPresent = Boolean(projectData.originalVideoUrl || projectData.captionedVideoUrl || projectData.generatedVideoUrl);
      
      // Skip if already initialized AND we have tracks AND video data hasn't changed
      if (hasInitializedRef.current && tracks.length > 0) {
        console.log('⏭️ Skipping re-initialization - already have tracks loaded');
        return;
      }
      
      // Also skip if this is a re-run triggered by video data loading, but we already loaded audio
      if (hasInitializedRef.current && videoDataPresent && tracks.length === 0) {
        console.log('⏭️ Skipping audio load - video project detected after initialization');
        return;
      }
      
      hasInitializedRef.current = true;
      
      // First, try to load existing saved tracks
      const hasExistingTracks = await loadProjectTracks();
      
      // Wait for project data to be fully loaded before checking video content
      // We need to wait for the project fetch effect to complete first
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if we have video content in the project data - prioritize video extraction
      const hasVideoInProject = projectData.originalVideoUrl || projectData.captionedVideoUrl || projectData.generatedVideoUrl;
      const hasVideoTracks = tracks.some(track => track.isVideo);
      console.log("audioUrl Data: ", audioUrl )
      console.log("video Data: ", projectData )
      console.log('🎬 Video check:', {
        hasVideoInProject,
        originalVideoUrl: projectData.originalVideoUrl,
        captionedVideoUrl: projectData.captionedVideoUrl,
        generatedVideoUrl: projectData.generatedVideoUrl,
        hasVideoTracks
      });
      
      // NOTE: We no longer skip audio loading when video is present
      // This allows users to add custom audio tracks to video projects
      // Video audio will only be extracted during export, not on timeline load
      
      // Check for standalone audio files
      console.log('🎵 Checking for standalone audio files to load...');
      
      // Determine which audio URL to use
      // Priority: URL parameter → finalAudio
      // NOTE: Skip originalAudioUrl if any video URL is present - video projects should not auto-load audio
      let audioSourceUrl = audioUrl;
      if (!audioSourceUrl && projectData.finalAudio) {
        audioSourceUrl = projectData.finalAudio;
        console.log('🎵 Using finalAudio from project:', projectData.finalAudio);
      } else if (!audioSourceUrl && !hasVideoInProject && projectData.originalAudioUrl) {
        // Only use originalAudioUrl as fallback if NO video content exists
        audioSourceUrl = projectData.originalAudioUrl;
        console.log('🎵 Using originalAudioUrl as fallback (no video present):', projectData.originalAudioUrl);
      } else if (!audioSourceUrl && hasVideoInProject && projectData.originalAudioUrl) {
        console.log('🎬 Video project detected - SKIPPING audio loading. originalAudioUrl exists but ignored:', projectData.originalAudioUrl);
      }
      
      // Only load initial audio from URL if no existing tracks were found and we have an audio source
      // CRITICAL: Do NOT load audio if this is a video project
      if (!hasExistingTracks && audioSourceUrl && audioSourceUrl.trim() !== '' && !hasVideoInProject) {
        setIsLoading(true);
        try {
          console.log('🎵 Loading audio from URL:', audioSourceUrl);
          // Fetch the audio file from S3 URL
          const response = await fetch(audioSourceUrl);
          if (!response.ok) {
            throw new Error('Failed to fetch audio file');
          }
          
          const blob = await response.blob();
          const file = new File([blob], 'initial-audio.mp3', { type: 'audio/mpeg' });
          
          // Load the audio buffer
          const audioBuffer = await loadAudioFile(file);
          
          const newTrack: AudioTrack = {
            id: `initial-track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: `Project Audio - ${projectId}`,
            url: audioSourceUrl, // Use the determined audio source URL
            audioBuffer,
            startTime: 0,
            duration: audioBuffer.duration,
            volume: 0.6,
            isMuted: false,
            color: generateTrackColor(),
            originalDuration: audioBuffer.duration,
            fadeInDuration: 0,
            fadeOutDuration: 0,
            trimStart: 0,
            trimEnd: audioBuffer.duration,
            laneIndex: 0, // Initial track goes to lane 0
            isSelected: false,
            isSolo: false,
          pan: 0
          };

          console.log('🎵 INITIAL TRACK CREATED:', {
            name: newTrack.name,
            duration: newTrack.duration,
            originalDuration: newTrack.originalDuration,
            trimStart: newTrack.trimStart,
            trimEnd: newTrack.trimEnd,
            expectedTrimEnd: audioBuffer.duration
          });

          setTracks([newTrack]);
          setTotalDuration(Math.max(60, audioBuffer.duration));
          
          // showNotification({
          //   title: 'Audio Loaded',
          //   message: 'Project audio loaded successfully',
          //   type: 'success'
          // });
        } catch (error) {
          console.error('Error loading initial audio:', error);
          showNotification({
            title: 'Load Error',
            message: 'Failed to load project audio. You can upload new audio files.',
            type: 'error'
          });
        } finally {
          setIsLoading(false);
        }
      } else if (!hasExistingTracks && !audioSourceUrl) {
        console.log('📝 No audio source available - empty project');
        // showNotification({
        //   title: 'No Audio Found',
        //   message: 'No audio found for this project. Upload audio files to start editing.',
        //   type: 'info'
        // });
      }
    };

    initializeProject();
  }, [projectId, audioUrl, projectData.finalAudio, projectData.originalAudioUrl, projectData.originalVideoUrl, projectData.captionedVideoUrl, projectData.generatedVideoUrl]); // Wait for all video/audio URLs to be loaded

  // Load project title
  useEffect(() => {
    const loadProjectTitle = async () => {
      if (!projectId) return;
      
      try {
        console.log('🔍 Loading project title for project ID:', projectId);
        
        const response = await fetch(getApiUrl(`/projects/${projectId}`), {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        
        console.log('📡 Project API response status:', response.status);
        
        if (response.ok) {
          const responseData = await response.json();
          console.log('📦 Project data received:', responseData);
          
          if (responseData && responseData.title) {
            console.log('✅ Setting project title to:', responseData.title);
            setProjectData(prev => ({ ...prev, title: responseData.title }));
          } else {
            console.log('⚠️ No title found in project data:', responseData);
          }
        } else {
          console.log('❌ Failed to fetch project:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('❌ Error loading project title:', error);
      }
    };
    
    loadProjectTitle();
  }, [projectId, token]); // Add token as dependency

  // Reset initialization flag when projectId changes
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [projectId]);

  // Fetch project details to get title
  useEffect(() => {
    if (!projectId) return;
    
    const fetchProjectDetails = async () => {
      try {
        console.log('🔍 Fetching project details for:', projectId);
        const response = await fetch(getApiUrl(`/projects/${projectId}`), {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        if (response.ok) {
          const responseData = await response.json();
          console.log('📦 Project data received:', responseData);
          if (responseData) {
            // Update projectData with all fields at once
            setProjectData(prev => ({
              ...prev,
              ...(responseData.title && { title: responseData.title }),
              ...(responseData.finalAudio && { finalAudio: responseData.finalAudio }),
              ...(responseData.originalAudioUrl && { originalAudioUrl: responseData.originalAudioUrl }),
              ...(responseData.originalVideoUrl && { originalVideoUrl: responseData.originalVideoUrl }),
              ...(responseData.generatedVideoUrl && { generatedVideoUrl: responseData.generatedVideoUrl }),
              ...(responseData.finalSoundAudio && { finalSoundAudio: responseData.finalSoundAudio }),
              ...(responseData.captionedVideoUrl && { captionedVideoUrl: responseData.captionedVideoUrl }),
              ...(responseData.thumbnail && { thumbnail: responseData.thumbnail }),
              ...(responseData.hasCaptions !== undefined && { hasCaptions: responseData.hasCaptions }),
              ...(responseData.titleData && { titleData: responseData.titleData })
            }));
            
            // Log what we found
            if (responseData.title) {
              console.log('✅ Setting project title:', responseData.title);
            }
            if (responseData.finalAudio) {
              console.log('✅ Setting finalAudio URL:', responseData.finalAudio);
            } else {
              console.log('❌ No finalAudio found in project data');
            }
            if (responseData.originalAudioUrl) {
              console.log('✅ Setting originalAudio URL:', responseData.originalAudioUrl);
            } else {
              console.log('❌ No originalAudioUrl found in project data');
            }
            if (responseData.titleData) {
              console.log('✅ Setting titleData:', responseData.titleData);
            }
            
            // Store video URLs for VerticalNav
            if (responseData.originalVideoUrl) {
              console.log('✅ Setting originalVideo URL:', responseData.originalVideoUrl);
            }
            if (responseData.generatedVideoUrl) {
              console.log('✅ Setting generatedVideo URL:', responseData.generatedVideoUrl);
            }
            if (responseData.captionedVideoUrl) {
              console.log('✅ Setting captionedVideo URL:', responseData.captionedVideoUrl);
            }
            if (responseData.thumbnail) {
              console.log('✅ Setting thumbnail URL:', responseData.thumbnail);
            }
            if (responseData.hasCaptions !== undefined) {
              console.log('✅ Setting hasCaptions:', responseData.hasCaptions);
            }
            if (responseData.finalSoundAudio !== undefined) {
              console.log('✅ Setting finalSoundAudio:', responseData.finalSoundAudio);
            }
            
            // Check if this project has video content
            // Priority order: captionedVideoUrl → generatedVideoUrl → originalVideoUrl
            // NOTE: We NO LONGER extract audio from video on timeline load to improve performance
            // Audio will only be extracted during the export process
            if (responseData.captionedVideoUrl || responseData.generatedVideoUrl || responseData.originalVideoUrl) {
              const videoUrl = responseData.captionedVideoUrl || responseData.generatedVideoUrl || responseData.originalVideoUrl;
              console.log('🎬 Project has video content:', videoUrl);
              console.log('🎬 Video URL priority - captioned:', responseData.captionedVideoUrl, 'generated:', responseData.generatedVideoUrl, 'original:', responseData.originalVideoUrl);
              console.log('⚡ SKIPPING audio extraction on load - will extract only during export for better performance');
              
              // Calculate video duration and set it
              const video = document.createElement('video');
              video.addEventListener('loadedmetadata', () => {
                const duration = Math.ceil(video.duration);
                console.log('📏 Video duration detected:', duration, 'seconds');
                setTotalDuration(duration);
              });
              video.addEventListener('error', (e) => {
                console.error('❌ Failed to load video for duration calculation:', e);
              });
              video.src = videoUrl;
              
              // Store video URL for export but don't extract audio now
              // The export process will handle audio extraction when user clicks "Export Video"
            }
          }
        } else {
          console.log('❌ Failed to fetch project data:', response.status);
        }
      } catch (error) {
        console.error('❌ Error fetching project details:', error);
      }
    };
    
    fetchProjectDetails();
  }, [projectId]);

  // Initialize audio context
  useEffect(() => {
    const initAudio = async () => {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        masterGainRef.current = audioContextRef.current.createGain();
        masterGainRef.current.connect(audioContextRef.current.destination);
        masterGainRef.current.gain.value = masterVolume;
      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    };
    
    initAudio();
    
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Auto-save removed - users must manually save their projects

  // Update master volume
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = isMasterMuted ? 0 : masterVolume;
    }
  }, [masterVolume, isMasterMuted]);

  // Animation loop for playhead
  useEffect(() => {
    if (isPlaying && audioContextRef.current) {
      let lastTime = currentTime;
      
      const updateTime = async () => {
        const elapsed = (audioContextRef.current!.currentTime - playStartTimeRef.current);
        const newTime = playStartOffsetRef.current + elapsed;
        
        if (newTime >= totalDuration) {
          setIsPlaying(false);
          setCurrentTime(0);
          // Stop all sources
          playingSourcesRef.current.forEach(source => {
            try {
              source.stop();
            } catch (e) {
              // Source might already be stopped
            }
          });
          playingSourcesRef.current = [];
        } else {
          setCurrentTime(newTime);
          
          // Check if any new tracks should start playing
          for (const track of tracks) {
            if (!track.isMuted) {
              const trackStartTime = track.startTime;
              const trackEndTime = track.startTime + track.duration;
              
              // Check if this track just started (playhead crossed the start time)
              if (lastTime < trackStartTime && newTime >= trackStartTime && newTime < trackEndTime) {
                console.log(`Starting track: ${track.name} at ${newTime.toFixed(2)}s`);
                
                // Load audio buffer if not already loaded
                const audioBuffer = await loadAudioBufferForTrack(track);
                
                if (audioBuffer) {
                  const source = audioContextRef.current!.createBufferSource();
                  const trackGainNode = audioContextRef.current!.createGain();
                  
                  source.buffer = audioBuffer;
                  source.connect(trackGainNode);
                  trackGainNode.connect(masterGainRef.current!);
                  
                  // Calculate timing first
                  const when = audioContextRef.current!.currentTime;
                  
                  // Set track volume with fade curves
                  const baseVolume = track.volume;
                  trackGainNode.gain.value = baseVolume;
                  
                  // Apply fade curves if configured
                  if (track.fadeInDuration > 0 || track.fadeOutDuration > 0) {
                    const trackRelativeTime = Math.max(0, newTime - trackStartTime);
                    const currentVolume = calculateVolumeWithFades(
                      trackRelativeTime, 
                      track.duration, 
                      baseVolume, 
                      track.fadeInDuration, 
                      track.fadeOutDuration
                    );
                    trackGainNode.gain.value = currentVolume;
                    
                    // Schedule fade automation for the remaining duration
                    scheduleFadeAutomation(trackGainNode, trackRelativeTime, track, when);
                  }
                  
                  // Calculate start offset within the track
                  const offset = Math.max(0, newTime - trackStartTime + track.trimStart);
                  
                  try {
                    source.start(when, offset);
                    playingSourcesRef.current.push(source);
                    
                    // Stop the source when track ends
                    const remainingDuration = track.duration - (newTime - trackStartTime);
                    source.stop(when + remainingDuration);
                    
                    console.log(`✅ Started track: ${track.name}, remaining duration: ${remainingDuration.toFixed(2)}s`);
                  } catch (e) {
                    console.error('Error starting delayed audio source:', e);
                  }
                }
              }
            }
          }
          
          lastTime = newTime;
          animationFrameRef.current = requestAnimationFrame(updateTime);
        }
      };
      animationFrameRef.current = requestAnimationFrame(updateTime);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, totalDuration, tracks]);

  // Effect to handle navigation to content creation after audio generation
  useEffect(() => {
    if (shouldNavigateToContentCreation && projectData.finalAudio && !showAudioMixer) {
      console.log('🎯 Audio generation complete, navigating to content creation...');
      
      // Reset the flag
      setShouldNavigateToContentCreation(false);
      
      // Navigate to content creation with the new final audio
      const contentCreationUrl = `/caption?project-id=${projectId}&audio-url=${encodeURIComponent(projectData.finalAudio)}`;
      
      // showNotification({
      //   title: 'Audio Generated Successfully!',
      //   message: 'Proceeding to content creation with your generated audio',
      //   type: 'success'
      // });
      
      navigate(contentCreationUrl);
    }
  }, [shouldNavigateToContentCreation, projectData.finalAudio, showAudioMixer, projectId, navigate, showNotification]);

  // Timeline width calculation based on zoom
  const timelineWidth = totalDuration * pixelsPerSecond * zoomLevel;

  // Smart time marker calculation based on zoom level
  const getTimeMarkerInterval = (zoomLevel: number): number => {
    if (zoomLevel >= 4) return 0.1;      // Every 0.1 seconds when very zoomed in
    if (zoomLevel >= 2) return 0.5;      // Every 0.5 seconds when zoomed in  
    if (zoomLevel >= 1) return 1;        // Every 1 second at normal zoom
    if (zoomLevel >= 0.5) return 5;      // Every 5 seconds when zoomed out
    if (zoomLevel >= 0.25) return 10;    // Every 10 seconds when more zoomed out
    return 30;                           // Every 30 seconds when very zoomed out
  };

  const formatTimeMarker = (seconds: number, interval: number): string => {
    if (interval < 1) {
      // For sub-second intervals, show decimal places
      return seconds.toFixed(1) + 's';
    } else if (interval < 60) {
      // For second intervals, show whole seconds
      return Math.round(seconds) + 's';
    } else {
      // For minute intervals, show minutes:seconds
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  };

  // Update snapToGridValue to use current state
  const snapToGrid_Current = (value: number): number => {
    return snapToGridValue(value, gridSize, snapToGrid);
  };

  // Scroll synchronization
  const handleTimelineScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (timeRulerRef.current && event.target !== timeRulerRef.current) {
      timeRulerRef.current.scrollLeft = (event.target as HTMLDivElement).scrollLeft;
    }
  };

  const handleRulerScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (timelineContainerRef.current && event.target !== timelineContainerRef.current) {
      timelineContainerRef.current.scrollLeft = (event.target as HTMLDivElement).scrollLeft;
    }
  };

  // Generate random color for new tracks
  const generateTrackColor = () => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    const usedColors = tracks.map(track => track.color);
    const availableColors = colors.filter(color => !usedColors.includes(color));
    return availableColors.length > 0 
      ? availableColors[0] 
      : colors[Math.floor(Math.random() * colors.length)];
  };

  // Load audio buffer for a track if not already loaded
  const loadAudioBufferForTrack = async (track: AudioTrack): Promise<AudioBuffer | null> => {
    if (track.audioBuffer) {
      return track.audioBuffer; // Already loaded
    }

    if (!audioContextRef.current) {
      console.error('Audio context not initialized');
      return null;
    }

    // Check if the URL is a blob URL (which won't work after reload)
    if (track.url.startsWith('blob:')) {
      console.warn(`Track "${track.name}" has invalid blob URL. Cannot load audio buffer.`);
      showNotification({
        title: 'Audio Loading Issue',
        message: `Track "${track.name}" has an invalid reference. Please re-upload the audio file.`,
        type: 'warning'
      });
      return null;
    }

    try {
      // Use the audio proxy utility to handle CORS issues
      console.log('🎵 Loading audio buffer via proxy for track:', track.name, track.url);
      const audioBuffer = await loadAuthenticatedAudioBuffer(track.url, audioContextRef.current);
      
      // Update the track with the loaded audio buffer
      setTracks(prev => prev.map(t => 
        t.id === track.id ? { ...t, audioBuffer } : t
      ));

      console.log('✅ Successfully loaded audio buffer for track:', track.name);
      return audioBuffer;
    } catch (error) {
      console.error('Error loading audio buffer for track:', track.name, error);
      showNotification({
        title: 'Audio Load Failed',
        message: `Could not load audio for "${track.name}". The file may have been moved or deleted.`,
        type: 'error'
      });
      return null;
    }
  };

  // Load audio file from File object (for uploads)
  const loadAudioFile = async (file: File): Promise<AudioBuffer> => {
    if (!audioContextRef.current) {
      throw new Error('Audio context not initialized');
    }

    const arrayBuffer = await file.arrayBuffer();
    return await audioContextRef.current.decodeAudioData(arrayBuffer);
  };

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Upload audio file to S3 and return the URL
  const uploadAudioFileToS3 = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('projectId', projectId || '');
    
    const response = await fetch(getApiUrl('/music-generation/upload-audio'), {
      method: 'POST',
      headers: {
        // 🔒 ADD AUTHENTICATION HEADER
        ...(token && { 'Authorization': `Bearer ${token}` }),
        // Note: Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload audio to S3');
    }

    const result = await response.json();
    return result.data.s3Url;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setIsLoading(true);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('audio/')) {
          showNotification({
            title: 'Invalid File',
            message: `${file.name} is not an audio file`,
            type: 'error'
          });
          continue;
        }

        // Upload to S3 first
        const s3Url = await uploadAudioFileToS3(file);
        
        // Then load the audio buffer for local playback
        const audioBuffer = await loadAudioFile(file);
        
        // Find available lane and calculate start time to avoid overlap
        const usedLanes = tracks.map(t => t.laneIndex);
        const availableLane = [0, 1, 2].find(lane => !usedLanes.includes(lane)) ?? Math.min(tracks.length, 2);
        
        // Calculate start time to avoid overlap with existing tracks in the same lane
        const tracksInLane = tracks.filter(t => t.laneIndex === availableLane);
        const maxEndTime = tracksInLane.length > 0 
          ? Math.max(...tracksInLane.map(t => t.startTime + t.duration))
          : 0;
        const newStartTime = maxEndTime > 0 ? maxEndTime + 0.5 : 0; // Add 0.5 second gap
        
        const newTrack: AudioTrack = {
          id: `track-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
          url: s3Url, // Use S3 URL instead of blob URL
          audioBuffer,
          startTime: newStartTime,
          duration: audioBuffer.duration,
          volume: 0.6,
          isMuted: false,
          color: generateTrackColor(),
          originalDuration: audioBuffer.duration,
          fadeInDuration: 0,
          fadeOutDuration: 0,
          trimStart: 0,
          trimEnd: audioBuffer.duration,
          laneIndex: availableLane,
          isSelected: false,
          isSolo: false,
          pan: 0
        };

        setTracks(prev => [...prev, newTrack]);
        setTotalDuration(prev => Math.max(prev, newStartTime + audioBuffer.duration));
      }

      // showNotification({
      //   title: 'Success',
      //   message: `${files.length} audio file(s) uploaded and loaded successfully`,
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Error uploading audio files:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to upload audio files. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Play/pause functionality - Enhanced version with buffer loading
  const togglePlayback = useCallback(async () => {
    if (!audioContextRef.current || !masterGainRef.current) return;

    if (isPlaying) {
      // Stop playback
      playingSourcesRef.current.forEach(source => {
        try {
          source.stop();
        } catch (e) {
          // Source might already be stopped
        }
      });
      playingSourcesRef.current = [];
      setIsPlaying(false);
      
      // Pause video if it exists
      if (videoRef.current) {
        videoRef.current.pause();
      }
    } else {
      // Start playback
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      playingSourcesRef.current = [];
      
      // Load audio buffers for tracks that need them and start playback
      for (const track of tracks) {
        if (!track.isMuted) {
          // Load audio buffer if not already loaded
          const audioBuffer = await loadAudioBufferForTrack(track);
          
          if (audioBuffer) {
            const trackStartTime = track.startTime;
            const trackEndTime = track.startTime + track.duration;
            
            // Check if track should be playing at current time
            if (currentTime >= trackStartTime && currentTime < trackEndTime) {
              const source = audioContextRef.current!.createBufferSource();
              const trackGainNode = audioContextRef.current!.createGain();
              
              source.buffer = audioBuffer;
              source.connect(trackGainNode);
              trackGainNode.connect(masterGainRef.current!);
              
              // Calculate timing first
              const when = audioContextRef.current!.currentTime;
              
              // Set track volume with fade curves
              const baseVolume = track.volume;
              trackGainNode.gain.value = baseVolume;
              
              // Apply fade curves if configured
              if (track.fadeInDuration > 0 || track.fadeOutDuration > 0) {
                const trackRelativeTime = Math.max(0, currentTime - trackStartTime);
                const currentVolume = calculateVolumeWithFades(
                  trackRelativeTime, 
                  track.duration, 
                  baseVolume, 
                  track.fadeInDuration, 
                  track.fadeOutDuration
                );
                trackGainNode.gain.value = currentVolume;
                
                // Schedule fade automation for the remaining duration
                scheduleFadeAutomation(trackGainNode, trackRelativeTime, track, when);
              }
              
              // Calculate start offset within the track
              const offset = Math.max(0, currentTime - trackStartTime + track.trimStart);
              
              try {
                source.start(when, offset);
                playingSourcesRef.current.push(source);
                
                // Stop the source when track ends
                const remainingDuration = track.duration - (currentTime - trackStartTime);
                source.stop(when + remainingDuration);
              } catch (e) {
                console.error('Error starting audio source:', e);
              }
            }
          }
        }
      }
      
      playStartTimeRef.current = audioContextRef.current.currentTime;
      playStartOffsetRef.current = currentTime;
      setIsPlaying(true);
      
      // Play video from current time if it exists
      if (videoRef.current) {
        videoRef.current.currentTime = currentTime;
        videoRef.current.play().catch(e => console.error('Error playing video:', e));
      }
    }
  }, [isPlaying, currentTime, tracks]);

  // Seek to specific time
  const seekTo = (time: number) => {
    const clampedTime = Math.max(0, Math.min(time, totalDuration));
    setCurrentTime(clampedTime);
    
    // Update video time if it exists
    if (videoRef.current) {
      videoRef.current.currentTime = clampedTime;
    }
    
    if (isPlaying) {
      // Restart playback from new position
      togglePlayback();
      setTimeout(() => togglePlayback(), 100);
    }
  };

  // Handle timeline click - Fixed for proper zoom behavior
  const handleTimelineClick = (event: React.MouseEvent) => {
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    // Calculate time based on actual timeline scaling
    const clickTime = (clickX / pixelsPerSecond) / zoomLevel;
    
    const newTime = snapToGrid_Current(Math.max(0, Math.min(clickTime, totalDuration)));
    seekTo(newTime);
  };

  // Zoom functions - Fixed to only affect timeline scale
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 10));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.1));
  };

  const resetZoom = () => {
    setZoomLevel(1);
  };

  // Track manipulation functions
  const selectTrack = (trackId: string) => {
    setTracks(prev => prev.map(track => ({
      ...track,
      isSelected: track.id === trackId
    })));
    setSelectedTrackId(trackId);
  };

  const deleteTrack = (trackId: string) => {
    const trackToDelete = tracks.find(track => track.id === trackId);
    
    // Prevent deletion of locked video tracks
    if (trackToDelete?.isLocked) {
      showNotification({
        title: 'Cannot Delete Video Track',
        message: 'Video tracks are locked and cannot be deleted. You can only play/stop them.',
        type: 'error'
      });
      return;
    }
    
    setTracks(prev => prev.filter(track => track.id !== trackId));
    if (selectedTrackId === trackId) {
      setSelectedTrackId(null);
    }
  };

  const duplicateTrack = (trackId: string) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;

    // Find an available lane or use the same lane
    const usedLanes = tracks.map(t => t.laneIndex);
    const availableLane = [0, 1, 2].find(lane => !usedLanes.includes(lane)) || track.laneIndex;

    const newTrack: AudioTrack = {
      ...track,
      id: `track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: `${track.name} (Copy)`,
      startTime: snapToGrid_Current(track.startTime + track.duration),
      color: generateTrackColor(),
      laneIndex: availableLane,
      isSelected: false
    };

    setTracks(prev => [...prev, newTrack]);
    // Extend timeline to accommodate the duplicated track
    setTotalDuration(prev => Math.max(prev, newTrack.startTime + newTrack.duration));
  };

  const handleSoundEffectAdd = async (soundEffectUrl: string, filename: string, duration: number) => {
    try {
      // Load the audio buffer from the S3 URL
      // Use appropriate proxy endpoint based on file type
      const proxyEndpoint = '/api/proxy-audio';
      const proxiedUrl = `${getApiUrl('')}${proxyEndpoint}?url=${encodeURIComponent(soundEffectUrl)}`;
      
      // Download through the proxy
      const response = await axios.get(proxiedUrl, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        }
      });
      
      //const response = await fetch(soundEffectUrl);
      if (!response.data) {
        throw new Error('Failed to fetch sound effect');
      }
      
      const arrayBuffer = await response.data.arrayBuffer();
      const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer);
      
      // Find available position at current playhead
      let targetLane = 0;
      let targetStartTime = currentTime;
      
      // Check if there's already a track at current time in each lane
      for (let lane = 0; lane <= 2; lane++) {
        const tracksInLane = tracks.filter(t => t.laneIndex === lane);
        const hasConflict = tracksInLane.some(track => 
          currentTime >= track.startTime && currentTime < (track.startTime + track.duration)
        );
        
        if (!hasConflict) {
          targetLane = lane;
          break;
        }
      }
      
      // If all lanes are occupied at current time, add at the end of timeline
      if (targetLane === 2) {
        const allTracks = tracks.filter(t => t.laneIndex === 2);
        const hasConflict = allTracks.some(track => 
          currentTime >= track.startTime && currentTime < (track.startTime + track.duration)
        );
        
        if (hasConflict) {
          // Find the end of the timeline
          const maxEndTime = Math.max(...tracks.map(t => t.startTime + t.duration), currentTime);
          targetStartTime = maxEndTime + 0.5; // Add small gap
          targetLane = 0; // Use first lane for end placement
        }
      }
      
      const newTrack: AudioTrack = {
        id: `sound-effect-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: filename.replace(/\.[^/.]+$/, '') || 'Sound Effect', // Remove extension
        url: soundEffectUrl,
        audioBuffer,
        startTime: snapToGrid_Current(targetStartTime),
        duration: audioBuffer.duration,
        volume: 0.5,
        isMuted: false,
        color: generateTrackColor(),
        originalDuration: audioBuffer.duration,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        trimStart: 0,
        trimEnd: audioBuffer.duration,
        laneIndex: targetLane,
        isSelected: false,
        isSolo: false,
        pan: 0
      };

      setTracks(prev => [...prev, newTrack]);
      setTotalDuration(prev => Math.max(prev, targetStartTime + audioBuffer.duration));
      
      // showNotification({
      //   title: 'Sound Effect Added',
      //   message: `"${newTrack.name}" added to timeline at ${targetStartTime.toFixed(1)}s`,
      //   type: 'success'
      // });
      
    } catch (error) {
      console.error('Error adding sound effect:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to add sound effect to timeline',
        type: 'error'
      });
    }
  };

  const handleOpenSoundEffectsModal = () => {
    setShowSoundEffectsModal(true);
  };

  const handleSoundEffectGenerated = (soundEffectUrl: string, filename: string, duration: number) => {
    // Add to timeline
    handleSoundEffectAdd(soundEffectUrl, filename, duration);
    // Refresh the sound effects list
    setSoundEffectsRefreshTrigger(prev => prev + 1);
  };

  // Handle opening AI Audio modal
  const handleOpenAIAudioModal = (type: 'upload' | 'prompts' | 'melody' = 'melody') => {
    setAudioModalType(type);
    setIsAudioModalOpen(true);
  };

  // Handle AI audio generation
  const handleAudioGenerated = (audioUrl: string, projectId: string, type: 'narration' | 'melody') => {
    console.log('AI Audio generated:', { audioUrl, projectId, type });
    
    // Show success notification
    // showNotification({
    //   title: 'Success',
    //   message: `${type === 'narration' ? 'Narration' : 'Melody'} generated successfully! Adding to timeline...`,
    //   type: 'success'
    // });
    
    // Close modal
    setIsAudioModalOpen(false);
    
    // Add the generated audio to the timeline
    handleAudioGeneratedToTimeline(audioUrl, type);
  };

  // Add generated audio to timeline
  const handleAudioGeneratedToTimeline = async (audioUrl: string, type: 'narration' | 'melody') => {
    try {
      // Use the audio proxy utility to handle CORS issues (same as loadAudioBufferForTrack)
      console.log('🎵 Loading generated audio buffer via proxy:', audioUrl);
      const audioBuffer = await loadAuthenticatedAudioBuffer(audioUrl, audioContextRef.current!);
      
      const trackName = `${type === 'narration' ? 'Narration' : 'ST'} - ${new Date().toLocaleTimeString()}`;
      
      // Find available lane and calculate start time
      const usedLanes = tracks.map(t => t.laneIndex);
      const availableLane = [0, 1, 2].find(lane => !usedLanes.includes(lane)) ?? Math.min(tracks.length, 2);
      
      const tracksInLane = tracks.filter(t => t.laneIndex === availableLane);
      const maxEndTime = tracksInLane.length > 0 
        ? Math.max(...tracksInLane.map(t => t.startTime + t.duration)) 
        : 0;
      const startTime = snapToGrid_Current(maxEndTime);

      const newTrack: AudioTrack = {
        id: `${type}-track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: trackName,
        url: audioUrl,
        audioBuffer,
        startTime: startTime,
        duration: audioBuffer.duration,
        volume: type === 'melody' ? 0.6 : 0.8, // Slightly lower volume for background music
        isMuted: false,
        color: type === 'melody' ? '#10b981' : '#3b82f6', // Green for melody, blue for narration
        originalDuration: audioBuffer.duration,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        trimStart: 0,
        trimEnd: audioBuffer.duration,
        laneIndex: availableLane,
        isSelected: false,
        isSolo: false,
        pan: 0
      };

      setTracks(prev => [...prev, newTrack]);
      setTotalDuration(Math.max(totalDuration, startTime + audioBuffer.duration));
      
      console.log('✅ Successfully added generated audio to timeline:', trackName);
    } catch (error) {
      console.error('Error adding AI audio to timeline:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to add generated audio to timeline',
        type: 'error'
      });
    }
  };

  const updateTrackVolume = (trackId: string, volume: number) => {
    setTracks(prev => prev.map(track => 
      track.id === trackId ? { ...track, volume } : track
    ));
  };

  const toggleTrackMute = (trackId: string) => {
    setTracks(prev => prev.map(track => {
      if (track.id === trackId) {
        if (track.isMuted) {
          // Unmuting: restore volume to 0.33 (33%) or previous value
          return { 
            ...track, 
            isMuted: false,
            volume: track.volume === 0 ? 0.6 : track.volume 
          };
        } else {
          // Muting: set volume to 0
          return { 
            ...track, 
            isMuted: true,
            volume: 0 
          };
        }
      }
      return track;
    }));
  };

  const splitTrack = async (trackId: string, splitTime?: number) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;

    // Load audio buffer if not already loaded
    const audioBuffer = await loadAudioBufferForTrack(track);
    if (!audioBuffer) {
      showNotification({
        title: 'Split Failed',
        message: 'Could not load audio data for splitting',
        type: 'error'
      });
      return;
    }

    const splitAt = splitTime ?? currentTime;
    const relativeTime = splitAt - track.startTime;
    if (relativeTime <= 0 || relativeTime >= track.duration) return;

    // Create two new tracks
    const firstTrack: AudioTrack = {
      ...track,
      audioBuffer, // Include the loaded audio buffer
      id: `track-${Date.now()}-1-${Math.random().toString(36).substr(2, 9)}`,
      duration: relativeTime,
      trimEnd: track.trimStart + relativeTime,
      name: `${track.name} (Part 1)`,
      isSelected: false
    };

    const secondTrack: AudioTrack = {
      ...track,
      audioBuffer, // Include the loaded audio buffer
      id: `track-${Date.now()}-2-${Math.random().toString(36).substr(2, 9)}`,
      startTime: snapToGrid_Current(splitAt),
      duration: track.duration - relativeTime,
      trimStart: track.trimStart + relativeTime,
      name: `${track.name} (Part 2)`,
      color: generateTrackColor(),
      isSelected: false
    };

    setTracks(prev => prev.filter(t => t.id !== trackId).concat([firstTrack, secondTrack]));
  };

  // Resize handle functionality for trimming tracks
  const handleResizeMouseDown = (trackId: string, side: 'left' | 'right', event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setDraggedTrack(trackId);
    setResizeMode(side);
    setIsDragging(true);
    
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!timelineContainerRef.current) return;
      
      const containerRect = timelineContainerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left + timelineContainerRef.current.scrollLeft;
      const mouseTime = mouseX / (pixelsPerSecond * zoomLevel);
      
      setTracks(prev => prev.map(currentTrack => {
        if (currentTrack.id !== trackId) return currentTrack;
        
        if (side === 'left') {
          // Resize from left (trim start)
          const newStartTime = Math.max(0, Math.min(mouseTime, currentTrack.startTime + currentTrack.duration - 0.1));
          const newDuration = (currentTrack.startTime + currentTrack.duration) - newStartTime;
          const newTrimStart = currentTrack.trimStart + (newStartTime - currentTrack.startTime);
          
          return {
            ...currentTrack,
            startTime: snapToGrid_Current(newStartTime),
            duration: newDuration,
            trimStart: Math.max(0, Math.min(newTrimStart, currentTrack.originalDuration - 0.1))
          };
        } else {
          // Resize from right (trim end)
          const maxEndTime = currentTrack.startTime + (currentTrack.originalDuration - currentTrack.trimStart);
          const newEndTime = Math.max(currentTrack.startTime + 0.1, Math.min(mouseTime, maxEndTime));
          const newDuration = newEndTime - currentTrack.startTime;
          const calculatedTrimEnd = currentTrack.trimStart + newDuration;
          
          return {
            ...currentTrack,
            duration: newDuration,
            trimEnd: calculatedTrimEnd
          };
        }
      }));
    };
    
    const handleMouseUp = () => {
      setDraggedTrack(null);
      setResizeMode(null);
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  const handleTrackMouseDown = (trackId: string, event: React.MouseEvent) => {
    if (event.button !== 0) return; // Only left mouse button
    
    const startX = event.clientX;
    const startY = event.clientY;
    let hasMoved = false;
    
    // Get the track's current position when drag starts
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    // Calculate the offset from where the user clicked within the track
    const trackElement = event.currentTarget as HTMLElement;
    const trackRect = trackElement.getBoundingClientRect();
    const clickOffsetX = event.clientX - trackRect.left;
    
    const handleMouseMove = (e: MouseEvent) => {
      const moveDistance = Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY);
      
      // Only start dragging if moved more than 5 pixels
      if (moveDistance > 5 && !hasMoved) {
        hasMoved = true;
        setDraggedTrack(trackId);
        setIsDragging(true);
      }
      
      if (hasMoved && timelineContainerRef.current) {
        const containerRect = timelineContainerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - containerRect.left + timelineContainerRef.current.scrollLeft;
        const mouseY = e.clientY - containerRect.top;
        
        // Calculate new position accounting for where the user grabbed the track
        const grabOffsetInTime = clickOffsetX / (pixelsPerSecond * zoomLevel);
        const newStartTime = Math.max(0, (mouseX / (pixelsPerSecond * zoomLevel)) - grabOffsetInTime);
        const newLane = Math.max(0, Math.min(2, Math.floor(mouseY / 90)));
        
        // Update track position immediately
        setTracks(prev => prev.map(currentTrack => 
          currentTrack.id === trackId 
            ? { 
                ...currentTrack, 
                startTime: snapToGrid_Current(newStartTime),
                laneIndex: newLane
              }
            : currentTrack
        ));
      }
    };
    
    const handleMouseUp = (e: MouseEvent) => {
      // If we didn't move much, treat it as a click
      if (!hasMoved) {
        selectTrack(trackId);
      } else {
        // If track was moved, calculate timeline extension from final position
        if (timelineContainerRef.current) {
          const containerRect = timelineContainerRef.current.getBoundingClientRect();
          const mouseX = e.clientX - containerRect.left + timelineContainerRef.current.scrollLeft;
          const grabOffsetInTime = clickOffsetX / (pixelsPerSecond * zoomLevel);
          const finalStartTime = Math.max(0, (mouseX / (pixelsPerSecond * zoomLevel)) - grabOffsetInTime);
          const snappedStartTime = snapToGrid_Current(finalStartTime);
          
          // Get the original track to know its duration
          if (track) {
            const trackEndTime = snappedStartTime + track.duration;
            setTotalDuration(prev => Math.max(prev, trackEndTime));
          }
        }
      }
      
      setDraggedTrack(null);
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Save project functionality - Updated to use API
  const saveProject = async () => {
    if (tracks.length === 0) {
      showNotification({
        title: 'Nothing to Save',
        message: 'Add some audio tracks before saving',
        type: 'error'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const projectData = {
        tracks: tracks.map(track => ({
          name: track.name,
          s3Url: track.url, // Frontend uses 'url', backend expects 's3Url'
          startTime: track.startTime,
          duration: track.duration,
          originalDuration: track.originalDuration,
          volume: track.volume,
          isMuted: track.isMuted,
          color: track.color,
          laneIndex: track.laneIndex,
          fadeInDuration: track.fadeInDuration,
          fadeOutDuration: track.fadeOutDuration,
          trimStart: track.trimStart,
          trimEnd: track.trimEnd,
          isSelected: track.isSelected,
          trackType: 'sound'  // NEW: Mark as sound track
        })),
        masterVolume: masterVolume,
        isMasterMuted: isMasterMuted,
        totalDuration: totalDuration,
        zoomLevel: zoomLevel,
        snapToGrid: snapToGrid,
        gridSize: gridSize
      };

      // Save to backend - Updated API URL
      const response = await fetch(getApiUrl(`/music-generation/projects/${projectId}/tracks`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save project');
      }

      const result = await response.json();
      
      // showNotification({
      //   title: 'Project Saved',
      //   message: `Successfully saved ${result.data.tracks?.length || 0} audio tracks`,
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Save error:', error);
      showNotification({
        title: 'Save Failed',
        message: error.message || 'Failed to save project. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Generate mixed audio blob for soundtrack export
  const generateMixedAudio = async (tracksToMix?: AudioTrack[]): Promise<Blob> => {
    if (!audioContextRef.current) {
      throw new Error('Audio context not initialized');
    }

    // Use provided tracks or default to component tracks
    const mixTracks = tracksToMix || tracks;

    const duration = totalDuration;
    const sampleRate = audioContextRef.current.sampleRate;
    const numberOfChannels = 2; // Stereo
    const length = sampleRate * duration;
    
    // Create offline audio context for mixing
    const offlineContext = new OfflineAudioContext(numberOfChannels, length, sampleRate);
    
    try {
      // Mix all provided tracks
      for (const track of mixTracks) {
        if (track.audioBuffer && !track.isMuted) {
          const source = offlineContext.createBufferSource();
          const gainNode = offlineContext.createGain();
          
          source.buffer = track.audioBuffer;
          source.connect(gainNode);
          gainNode.connect(offlineContext.destination);
          
          // Apply track volume and master volume
          gainNode.gain.value = track.volume * masterVolume;
          
          // Calculate trim parameters
          const trimStart = track.trimStart || 0;
          const trimEnd = track.trimEnd || track.audioBuffer.duration;
          const trimDuration = trimEnd - trimStart;
              
          // Start at the track's start time, with offset for trim start, for the trimmed duration
          source.start(track.startTime, trimStart, trimDuration);
        }
      }
      
      // Render the mixed audio
      const renderedBuffer = await offlineContext.startRendering();
      
      // Convert to WAV blob
      const wavBlob = audioBufferToWav(renderedBuffer);
      return wavBlob;
      
    } catch (error) {
      console.error('Error generating mixed audio:', error);
      throw new Error('Failed to generate mixed audio');
    }
  };

  // Helper function to convert AudioBuffer to WAV blob
  const audioBufferToWav = (buffer: AudioBuffer): Blob => {
    const numberOfChannels = buffer.numberOfChannels;
    const length = buffer.length * numberOfChannels * 2 + 44;
    const arrayBuffer = new ArrayBuffer(length);
    const view = new DataView(arrayBuffer);
    const channels = [];
    
    // Get channel data
    for (let i = 0; i < numberOfChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }
    
    // WAV header
    let offset = 0;
    const writeString = (str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
      offset += str.length;
    };
    
    const writeUint32 = (data: number) => {
      view.setUint32(offset, data, true);
      offset += 4;
    };
    
    const writeUint16 = (data: number) => {
      view.setUint16(offset, data, true);
      offset += 2;
    };
    
    writeString('RIFF');
    writeUint32(length - 8);
    writeString('WAVE');
    writeString('fmt ');
    writeUint32(16);
    writeUint16(1);
    writeUint16(numberOfChannels);
    writeUint32(buffer.sampleRate);
    writeUint32(buffer.sampleRate * 2 * numberOfChannels);
    writeUint16(numberOfChannels * 2);
    writeUint16(16);
    writeString('data');
    writeUint32(length - offset - 4);
    
    // Interleave samples
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  // Export/download functionality
  const exportAudio = async () => {
    console.log('🎵 [EXPORT AUDIO] Function called');
    
    // Check for available audio - prioritize finalAudio, fallback to finalSoundAudio
    const audioUrl = projectData.finalAudio || projectData.finalSoundAudio;
    
    console.log('🎵 [EXPORT AUDIO] Audio URL check:', {
      finalAudio: projectData.finalAudio,
      finalSoundAudio: projectData.finalSoundAudio,
      selectedUrl: audioUrl
    });
    
    if (!audioUrl) {
      console.log('❌ [EXPORT AUDIO] No audio URL available');
      showNotification({
        title: 'No Audio Available',
        message: 'Please create a mixed audio first using the Mix Audio feature',
        type: 'error'
      });
      return;
    }

    console.log('🎵 [EXPORT AUDIO] Starting download process');
    setIsLoading(true);

    try {
      const filename = `${projectData.title.replace(/[^a-zA-Z0-9]/g, '_')}_final_mix.wav`;
      
      console.log('🎵 [EXPORT AUDIO] Calling  with:', {
        url: audioUrl,
        filename,
        hasToken: !!token
      });

      await downloadAudio(audioUrl, {
        filename: filename,
        onStart: () => {
          console.log('📥 [EXPORT AUDIO] Calling ');
        },
        onComplete: () => {
          console.log('✅ [SOUNDTRACK] Audio downloaded successfully');
          showNotification({
            title: 'Audio Ready!',
            message: 'Your Audio with soundtrack has been downloaded',
            type: 'success'
          });
        },
        onError: (error) => {
          console.error('❌ [SOUNDTRACK] Failed to download Audio:', error);
          showNotification({
            title: 'Download Failed',
            message: 'Audio was created but download failed. Use the download button to try again.',
            type: 'warning'
          });
        }
      });
                  
      console.log('✅ [EXPORT AUDIO]  completed');
    } catch (error) {
      console.error('❌ [EXPORT AUDIO] Exception in try/catch:', error);
      showNotification({
        title: 'Download Failed',
        message: 'Failed to download the mixed audio. Please try again or contact support.',
        type: 'error'
      });
    } finally {
      console.log('🎵 [EXPORT AUDIO] Cleaning up, setting loading to false');
      setIsLoading(false);
    }
  };

  const selectedTrack = tracks.find(track => track.id === selectedTrackId);

  // Handle vertical navigation
  const handleNavSelected = (key: StepIndicatorNames) => {
    console.log('🔄 Song Editor navigation:', key, 'Project ID:', projectId);
    
    // Handle navigation based on the selected step
    switch (key) {
      case StepIndicatorNames.ScriptGeneration:
        // Navigate to script generator for this project
        if (projectId) {
          console.log('✅ Navigating to script generator for project:', projectId);
          navigate(`/script-generator/${projectId}`);
        } else {
          console.log('⚠️ No project ID, navigating to general script generator');
          navigate('/script-generator');
        }
        break;
      case StepIndicatorNames.IdeaCreation:
        // Navigate to prompt generator for this project
        if (projectId) {
          console.log('✅ Navigating to prompt generator for project:', projectId);
          navigate(`/prompt-generator/${projectId}`);
        } else {
          console.log('⚠️ No project ID, navigating to general prompt generator');
          navigate('/prompt-generator');
        }
        break;
      case StepIndicatorNames.AudioGeneration:
        // Navigate to standalone audio generation
        navigate('/audio-generation');
        break;
      case StepIndicatorNames.ContentCreation:
        // Navigate to video editor for content creation
        if (projectId) {
          console.log('✅ Navigating to video editor for project:', projectId);
          navigate(`/video-editor/${projectId}`);
        } else {
          console.log('⚠️ No project ID, navigating to video editor');
          navigate('/video-editor');
        }
        break;
      case StepIndicatorNames.CaptionCreation:
        // Navigate to caption editor for this project
        if (projectId) {
          console.log('✅ Navigating to caption editor for project:', projectId);
          navigate(`/caption?project-id=${projectId}`);
        } else {
          console.log('⚠️ No project ID, navigating to caption editor');
          navigate('/caption');
        }
        break;
      case StepIndicatorNames.SoundGeneration:
        // Already in sound editor - no action needed
        console.log('ℹ️ Already in sound editor - staying here');
        break;
      case StepIndicatorNames.TitleDescTag:
        // Open title description modal with soundtrack video if available
        if (projectId) {
          console.log('✅ Opening title description modal for project:', projectId);
          if (soundtrackVideoUrl) {
            // Use the soundtrack-mixed video
            console.log('🎵 Using soundtrack video for title generation:', soundtrackVideoUrl);
            openTitleModal('video', soundtrackVideoUrl, projectId, projectData.titleData);
          } else {
            // Fall back to project data or no media
            console.log('⚠️ No soundtrack video available, using fallback');
            openTitleModal('none', undefined, projectId, projectData.titleData);
          }
        }
        break;
      case StepIndicatorNames.ThumbnailCreation:
        // Navigate to thumbnail creation for this project
        if (projectId) {
          console.log('✅ Navigating to thumbnail editor for project:', projectId);
          navigate(`/thumbnail-editor/${projectId}`);
        } else {
          console.log('⚠️ No project ID, navigating to thumbnail landing');
          navigate('/thumbnail-landing');
        }
        break;
      case StepIndicatorNames.Export:
        // Handle export
        console.log('🚧 Export functionality not implemented yet');
        break;
      default:
        console.log('❓ Unknown navigation key:', key);
        break;
    }
  };

  return (
    <div className={styles.audioEditorContainer}>
      <Navigation />
      <VerticalNav 
        handleNavSelected={handleNavSelected} 
        initialStep={StepIndicatorNames.SoundGeneration}
        projectData={{
          originalAudioUrl: projectData.originalAudioUrl,
          finalAudio: projectData.finalAudio,
          originalVideoUrl: projectData.originalVideoUrl,
          generatedVideoUrl: projectData.generatedVideoUrl,
          captionedVideoUrl: projectData.captionedVideoUrl,
          thumbnail: projectData.thumbnail,
          title: projectData.title,
          hasCaptions: projectData.hasCaptions,
          titleData: projectData.titleData
        }}
      />
      
      <div className={styles.editorContent}>
        {/* Header */}
        <div className={styles.header}>
          <Button 
            className={styles.backButton}
            onClick={() => navigate('/sound-generation')}
            title="Back to Sound Generation"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          
          <div className={styles.titleContainer}>
            {isEditingProjectTitle ? (
              <input
                type="text"
                value={projectData.title}
                onChange={(e) => setProjectData(prev => ({ ...prev, title: e.target.value }))}
                onBlur={() => handleUpdateProjectTitle(projectData.title)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUpdateProjectTitle(projectData.title);
                  if (e.key === 'Escape') setIsEditingProjectTitle(false);
                }}
                className={styles.projectTitleInput}
                autoFocus
              />
            ) : (
              <>
                <h1 
                  className={`${styles.pageTitle} ${styles.clickableTitle}`}
                  onClick={() => navigate('/sound-generation')}
                  title={t('backButton')}
                >
                  {t('pageTitle')}
                </h1>
                <div className={styles.projectNameContainer}>
                  <h2 className={styles.projectName}>{projectData.title}</h2>
                  <button 
                    className={styles.editButton}
                    onClick={() => setIsEditingProjectTitle(true)}
                    title={t('editProjectTitle')}
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                </div>
              </>
            )}
          </div>
          
          <div className={styles.headerActions}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginRight: '20px' }}>
              <Text size="sm">{t('header.master')}</Text>
              <ActionIcon
                onClick={() => setIsMasterMuted(!isMasterMuted)}
                className={isMasterMuted ? styles.muteButtonActive : styles.muteButton}
              >
                <FontAwesomeIcon icon={isMasterMuted ? faVolumeMute : faVolumeUp} />
              </ActionIcon>
              <input
                type="range"
                value={masterVolume * 100}
                onChange={(event) => setMasterVolume(Number(event.target.value) / 100)}
                min={0}
                max={100}
                disabled={isMasterMuted}
                style={{
                  width: '80px',
                  height: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '3px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <Text size="xs" style={{ minWidth: '35px', textAlign: 'center' }}>
                {Math.round(masterVolume * 100)}%
              </Text>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginRight: '20px' }}>
              <Button
                leftSection={<FontAwesomeIcon icon={faMagicWandSparkles} />}
                onClick={() => handleOpenAIAudioModal('melody')}
                disabled={isLoading}
                className={styles.aiSoundtrackButton}
                size="sm"
              >
                {t('header.aiSoundtrack')}
              </Button>
              
              <Button
                leftSection={<FontAwesomeIcon icon={faUpload} />}
                onClick={handleFileUpload}
                disabled={isLoading}
                className={styles.uploadButton}
                size="sm"
              >
                {t('header.uploadAudio')}
              </Button>
            
              <Button
                leftSection={<FontAwesomeIcon icon={faSave} />}
                onClick={saveProject}
                disabled={isLoading}
                className={styles.saveButton}
                size="sm"
              >
 
                {t('header.save')} 
                { isLoading && (<FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: "8px" }} />) }
              </Button>
              
              {/* Mix Audio Button - Show only when multiple audio tracks and no video */}
              {(() => {
                const hasMultipleAudioTracks = tracks.length > 0;
                const hasVideoContent = Boolean(
                  projectData.originalVideoUrl || 
                  projectData.captionedVideoUrl || 
                  projectData.generatedVideoUrl ||
                  tracks.some(track => track.isVideo)
                );
                
                return hasMultipleAudioTracks && !hasVideoContent ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                      leftSection={<FontAwesomeIcon icon={faSliders} />}
                      onClick={() => setShowAudioMixer(true)}
                      disabled={isLoading}
                      className={styles.mixButton}
                      size="sm"
                    >
                      {t('header.mixAudio')}
                    </Button>
                    
                    {/* Download Button - Show when we have audio to download */}
                    {(projectData.finalAudio || projectData.finalSoundAudio || tracks.length > 0) && (
                      <div               
                        onClick={exportAudio}               
                        style={{                 
                          width: '40px',                 
                          height: '40px',                 
                          borderRadius: '50%',                 
                          backgroundColor: '#10b981',                 
                          display: 'flex',                 
                          alignItems: 'center',                 
                          justifyContent: 'center',                 
                          cursor: 'pointer',                 
                          transition: 'all 0.2s ease',                 
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'               
                        }}               
                        onMouseEnter={(e) => {                 
                          e.currentTarget.style.backgroundColor = '#059669';                 
                          e.currentTarget.style.transform = 'scale(1.05)';               
                        }}               
                        onMouseLeave={(e) => {                 
                          e.currentTarget.style.backgroundColor = '#10b981';                 
                          e.currentTarget.style.transform = 'scale(1)';               
                        }}               
                        title={t('header.downloadMixedAudio')}
                      >               
                        <FontAwesomeIcon                  
                          icon={faDownload}                  
                          style={{                    
                            color: 'white',                   
                            fontSize: '18px'                 
                          }}               
                        />             
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
              
              {/* Debug: Rendering Export button check - finalAudio: {projectData.finalAudio} */}

              {/* Soundtrack Manager for Video Projects */}
              <SoundtrackManager
                projectId={projectId || ''}
                token={token}
                tracks={tracks}
                masterVolume={masterVolume}
                generateMixedAudio={generateMixedAudio}
                onSoundtrackReady={(videoUrl) => setSoundtrackVideoUrl(videoUrl)}
              />
            </div>
          </div>
        </div>

        <div className={styles.editorMain}>
          {/* Main Timeline Section */}
          <div className={styles.timelineSection}>
            {/* Video Player */}
            {((tracks.find(t => t.isVideo)?.url) || projectData.originalVideoUrl || projectData.captionedVideoUrl || projectData.generatedVideoUrl) && (
              <div className={styles.videoPlayerContainer}>
                <video
                  ref={videoRef}
                  src={projectData.captionedVideoUrl || projectData.generatedVideoUrl || projectData.originalVideoUrl || tracks.find(t => t.isVideo)?.url}
                  className={styles.videoPlayer}
                  // muted
                  playsInline
                />
              </div>
            )}

            {/* Timeline Controls - Moved between video and timeline */}
            <div className={styles.timelineControls}>
              <div className={styles.playbackControls}>
                <ActionIcon
                  size="lg"
                  onClick={togglePlayback}
                  color={isPlaying ? 'red' : 'blue'}
                  disabled={tracks.length === 0 && !projectData.originalVideoUrl && !projectData.captionedVideoUrl && !projectData.generatedVideoUrl}
                >
                  <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                </ActionIcon>
                
                <ActionIcon
                  size="lg"
                  onClick={() => {
                    playingSourcesRef.current.forEach(source => {
                      try { source.stop(); } catch (e) {}
                    });
                    playingSourcesRef.current = [];
                    setIsPlaying(false);
                    setCurrentTime(0);
                  }}
                  style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#f87171' }}
                >
                  <FontAwesomeIcon icon={faStop} />
                </ActionIcon>
                
                <Text size="sm" className={styles.timeDisplay}>
                  {formatTime(currentTime)} / {formatTime(totalDuration)}
                </Text>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {/* Track Action Buttons - Only show when track is selected */}
                {selectedTrackId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '15px', paddingRight: '15px', borderRight: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <ActionIcon
                      onClick={() => duplicateTrack(selectedTrackId)}
                      title={t('playback.duplicateTrack')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        borderRadius: '8px',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <FontAwesomeIcon icon={faCopy} size="sm" />
                    </ActionIcon>
                    
                    <ActionIcon
                      onClick={() => splitTrack(selectedTrackId)}
                      title={t('playback.splitTrack')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        borderRadius: '8px',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <FontAwesomeIcon icon={faCut} size="sm" />
                    </ActionIcon>
                    
                    <ActionIcon
                      onClick={() => deleteTrack(selectedTrackId)}
                      title={t('playback.deleteTrack')}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#f87171',
                        borderRadius: '8px',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </ActionIcon>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'white', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={snapToGrid}
                      onChange={(event) => setSnapToGrid(event.target.checked)}
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: snapToGrid ? '#3b82f6' : 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        position: 'relative',
                        margin: '0',
                        verticalAlign: 'middle',
                        flexShrink: 0
                      }}
                    />
                    <style>{`
                      input[type="checkbox"]:checked::after {
                        content: '✓';
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        color: white;
                        font-size: 12px;
                        font-weight: bold;
                        line-height: 1;
                      }
                    `}</style>
                    <span style={{ lineHeight: '16px' }}>Snap</span>
                  </label>
                  
                  <input
                    type="number"
                    value={gridSize}
                    onChange={(event) => setGridSize(Number(event.target.value) || 1)}
                    min={0.1}
                    max={10}
                    step={0.1}
                    disabled={!snapToGrid}
                    style={{
                      width: '70px',
                      height: '32px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      padding: '4px 8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <Text size="xs" color="dimmed">s</Text>
                </div>
                
                <div className={styles.zoomControls}>
                  <ActionIcon onClick={zoomOut} disabled={zoomLevel <= 0.1}>
                    <FontAwesomeIcon icon={faSearchMinus} />
                  </ActionIcon>
                  <Text size="xs" style={{ minWidth: '45px', textAlign: 'center' }}>
                    {Math.round(zoomLevel * 100)}%
                  </Text>
                  <ActionIcon onClick={zoomIn} disabled={zoomLevel >= 10}>
                    <FontAwesomeIcon icon={faSearchPlus} />
                  </ActionIcon>
                  <ActionIcon onClick={resetZoom} title={t('playback.resetZoom')}>
                    <FontAwesomeIcon icon={faMagic} />
                  </ActionIcon>
                </div>
              </div>
            </div>

            {/* Main Timeline Area */}
            <div className={styles.timelineArea}>
              {/* Time ruler - Adaptive markers based on zoom */}
              <div 
                ref={timeRulerRef}
                className={styles.timeRuler}
                onScroll={handleRulerScroll}
              >
                <div 
                  style={{ 
                    width: `${timelineWidth}px`,
                    height: '100%',
                    position: 'relative',
                    minWidth: '100%'
                  }}
                >
                  {(() => {
                    const interval = getTimeMarkerInterval(zoomLevel);
                    const markerCount = Math.ceil(totalDuration / interval);
                    
                    return Array.from({ length: markerCount + 1 }, (_, i) => {
                      const timeValue = i * interval;
                      if (timeValue > totalDuration) return null;
                      
                      return (
                        <div
                          key={`marker-${i}-${interval}`}
                          className={styles.timeMarker}
                          style={{ left: `${timeValue * pixelsPerSecond * zoomLevel}px` }}
                        >
                          <span>{formatTimeMarker(timeValue, interval)}</span>
                        </div>
                      );
                    }).filter(Boolean);
                  })()}
                </div>
              </div>

              {/* Timeline Container */}
              <div 
                ref={timelineContainerRef}
                className={styles.timelineContainer}
                onScroll={handleTimelineScroll}
              >
                {/* Timeline - Fixed width based on zoom */}
                <div
                  ref={timelineRef}
                  className={styles.timeline}
                  onClick={handleTimelineClick}
                  style={{ 
                    width: `${timelineWidth}px`,
                    minWidth: '100%',
                    position: 'relative'
                  }}
                >
                  {/* Playhead - Fixed positioning */}
                  <div
                    ref={playheadRef}
                    className={styles.playhead}
                    style={{ left: `${currentTime * pixelsPerSecond * zoomLevel}px` }}
                  />

                  {/* Grid lines for snapping */}
                  {snapToGrid && Array.from({ length: Math.ceil(totalDuration / gridSize) + 1 }, (_, i) => (
                    <div
                      key={`grid-${i}`}
                      style={{
                        position: 'absolute',
                        left: `${i * gridSize * pixelsPerSecond * zoomLevel}px`,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        pointerEvents: 'none'
                      }}
                    />
                  ))}

                  {/* Audio Tracks */}
                  {[0, 1, 2].map(laneIndex => (
                    <div
                      key={laneIndex}
                      className={styles.trackLane}
                      style={{ 
                        top: `${laneIndex * 90}px`,
                        width: '100%',
                        height: '85px'
                      }}
                    >
                      <div className={styles.laneLabel}>Track {laneIndex + 1}</div>
                      
                      {/* Loading indicator outside the track */}
                      {tracks.filter(track => track.laneIndex === laneIndex && track.isVideo && track.name.includes('Loading')).length > 0 && (
                        <div className={styles.externalLoadingIndicator}>
                          <div className={styles.loadingSpinnerExternal}>🔄</div>
                          <span>Loading video audio...</span>
                        </div>
                      )}
                      
                      {/* Track Lane Background */}
                      <div className={styles.laneBackground} />
                      
                      {tracks
                        .filter(track => track.laneIndex === laneIndex)
                        .map(track => (
                          <div
                            key={track.id}
                            className={`${styles.audioTrack} ${track.isSelected ? styles.selected : ''}`}
                            style={{
                              left: `${track.startTime * pixelsPerSecond * zoomLevel}px`,
                              width: `${track.duration * pixelsPerSecond * zoomLevel}px`,
                              backgroundColor: track.color,
                              opacity: track.isMuted ? 0.5 : (draggedTrack === track.id ? 0.8 : 1),
                              border: draggedTrack === track.id ? '2px dashed rgba(59, 130, 246, 0.8)' : '2px solid transparent',
                              cursor: 'grab',
                              userSelect: 'none',
                              transition: draggedTrack === track.id ? 'none' : 'all 0.1s ease'
                            }}
                            onMouseDown={(e) => handleTrackMouseDown(track.id, e)}
                          >
                            <div className={styles.trackContent}>
                              <div className={styles.trackName}>
                                {track.name}
                              </div>
                              <div className={styles.trackWaveform}>
                                {Array.from({ length: Math.max(5, Math.floor(track.duration * 10)) }, (_, i) => (
                                  <div
                                    key={`${track.id}-waveform-${i}`}
                                    className={styles.waveformBar}
                                    style={{ height: `${Math.random() * 80 + 20}%` }}
                                  />
                                ))}
                              </div>
                            </div>
                            
                            {/* Keep simple overlay on track */}
                            {track.isVideo && track.name.includes('Loading') && (
                              <div className={styles.trackLoadingOverlay}></div>
                            )}
                            
                            <div 
                              className={styles.resizeHandleLeft}
                              onMouseDown={(e) => handleResizeMouseDown(track.id, 'left', e)}
                              style={{ cursor: 'ew-resize' }}
                            />
                            <div 
                              className={styles.resizeHandleRight}
                              onMouseDown={(e) => handleResizeMouseDown(track.id, 'right', e)}
                              style={{ cursor: 'ew-resize' }}
                            />

                            {/* Fade Overlays */}
                            {track.fadeInDuration > 0 && (
                              <div
                                className={styles.fadeInOverlay}
                                style={{
                                  width: `${(track.fadeInDuration / track.duration) * 100}%`,
                                }}
                              >
                                <div className={styles.fadeOverlayLabel}>Fade In</div>
                              </div>
                            )}
                            
                            {track.fadeOutDuration > 0 && (
                              <div
                                className={styles.fadeOutOverlay}
                                style={{
                                  width: `${(track.fadeOutDuration / track.duration) * 100}%`,
                                }}
                              >
                                <div className={styles.fadeOverlayLabel}>Fade Out</div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Always Visible Tabbed Interface */}
          <TabContainer
            selectedTrack={selectedTrack}
            currentTime={currentTime}
            onVolumeChange={updateTrackVolume}
            onMuteToggle={toggleTrackMute}
            onDuplicate={duplicateTrack}
            onSplit={splitTrack}
            onDelete={deleteTrack}
            onFadeChange={(trackId, property, value) => {
              setTracks(prev => prev.map(track => 
                track.id === trackId 
                  ? { ...track, [property]: value }
                  : track
              ));
            }}
            onSoundEffectAdd={handleSoundEffectAdd}
            onOpenSoundEffectsModal={handleOpenSoundEffectsModal}
            userId={user?.id || ''}
            projectId={projectId} // Pass projectId to TabContainer
            refreshTrigger={soundEffectsRefreshTrigger}
          />
        </div>

        {/* Empty State - Only show if no tracks AND no video */}
        {tracks.length === 0 && !isLoading && !projectData.originalVideoUrl && !projectData.captionedVideoUrl && !projectData.generatedVideoUrl && (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faUpload} size="3x" color="#ccc" />
            <h3>{t('emptyState.title')}</h3>
            <p>{t('emptyState.message')}</p>
            <Button
              leftSection={<FontAwesomeIcon icon={faUpload} />}
              onClick={handleFileUpload}
              size="lg"
              className={styles.emptyStateButton}
            >
              {t('emptyState.uploadButton')}
            </Button>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Audio Mixer Modal */}
      <AudioMixer
        tracks={tracks}
        totalDuration={totalDuration}
        masterVolume={masterVolume}
        isMasterMuted={isMasterMuted}
        isVisible={showAudioMixer}
        onClose={() => setShowAudioMixer(false)}
        projectId={projectId}
        finalSoundAudio={projectData.finalSoundAudio}
        isAudioOnlyWorkflow={!Boolean(
          projectData.originalVideoUrl || 
          projectData.captionedVideoUrl || 
          projectData.generatedVideoUrl ||
          tracks.some(track => track.isVideo)
        )}
      />

      {/* Sound Effects Generation Modal */}
      <SoundEffectsModal
        isOpen={showSoundEffectsModal}
        onClose={() => setShowSoundEffectsModal(false)}
        onSoundEffectGenerated={handleSoundEffectGenerated}
        userId={user?.id || ''}
      />

      {/* AI Audio Generation Modal */}
      <AudioModal
        isOpen={isAudioModalOpen}
        onClose={() => setIsAudioModalOpen(false)}
        onAudioGenerated={handleAudioGenerated}
        type={audioModalType}
        currentProjectId={projectId} // Use the renamed prop
      />

      {/* Audio Selection Modal for Caption Creation */}
      {/* Debug: Rendering AudioSelectionModal with isOpen: {isAudioSelectionModalOpen}, projectId: {projectId || ''}, finalAudio: {projectData.finalAudio}, originalAudioUrl: {projectData.originalAudioUrl} */}
      
      {/* Audio Selection Modal for Caption Creation */}
      <AudioSelectionModal
        isOpen={isAudioSelectionModalOpen}
        onClose={() => setIsAudioSelectionModalOpen(false)}
        projectId={projectId || ''}
        finalAudioUrl={projectData.finalAudio}
        originalAudioUrl={projectData.originalAudioUrl}
        projectTitle={projectData.title}
        tracks={tracks}
        onGenerateAudio={() => {
          console.log('🎵 Triggering audio generation from content creation modal');
          
          // Check if there are tracks to mix
          if (tracks.length === 0) {
            showNotification({
              title: 'No Audio Tracks',
              message: 'Please add some audio tracks before generating audio. Use the AI Soundtrack or Sound Effects features to create content.',
              type: 'error'
            });
            return;
          }
          
          setShouldNavigateToContentCreation(true); // Set flag to navigate after generation
          setShowAudioMixer(true); // Open the Mix Audio modal
        }}
      />

      {/* Title Generation Modal */}
      <TitleGenerationModal 
        isOpen={false}
        onClose={() => {}}
        onProcessContent={async () => ''}
        mediaType="audio"
        isProcessing={false}
        showDashboard={false}
        jobId={null}
        onCompletionCallback={() => {}}
      />
    </div>
  );
};

export default AudioEditor;
